/**
 * GraphQL type definitions matching backend schema — maturityAssessment pillar.
 * Split from graphql/types.ts (issue #1409).
 */

import type { PageInfo } from './common';

export enum MaturityFrameworkStatus {
  Active = 'Active',
  Draft = 'Draft',
  Deprecated = 'Deprecated',
}

export enum MaturityAssessmentStatus {
  Draft = 'Draft',
  InProgress = 'InProgress',
  Complete = 'Complete',
  Approved = 'Approved',
}

export enum MaturitySubjectEntityType {
  Service = 'Service',
  Team = 'Team',
}

export interface MaturityLevel {
  level: number;
  name: string;
  description: string;
  /** Binary checklist items that must be true for this level to be considered met. */
  criteria?: string[] | null;
}

export interface MaturityCapability {
  id: string;
  name: string;
  order: number;
  levels: MaturityLevel[];
}

export interface MaturityCategory {
  id: string;
  name: string;
  order: number;
  capabilities: MaturityCapability[];
}

export interface MaturityArea {
  id: string;
  name: string;
  order: number;
  categories: MaturityCategory[];
}

export interface MaturityFramework {
  entityId: string;
  entityType: string;
  name: string;
  description?: string | null;
  version: string;
  areas: MaturityArea[];
  status: MaturityFrameworkStatus;
  effectiveFrom: string;
  effectiveTo?: string | null;
  _schemaVersion: string;
  _createdAt: string;
  _createdBy: string;
  _updatedAt: string;
  _updatedBy: string;
}

export interface AreaScore {
  areaId: string;
  score: number;
}

export interface CategoryScore {
  categoryId: string;
  score: number;
}

export interface MaturityResponse {
  capabilityId: string;
  level: number;
  comment?: string | null;
  respondedAt: string;
}

export interface MaturityAssessment {
  entityId: string;
  entityType: string;
  name: string;
  description?: string | null;
  frameworkId: string;
  frameworkVersion: string;
  pollId: string;
  subjectEntityType: MaturitySubjectEntityType;
  subjectEntityId: string;
  assessmentPeriod: string;
  assessmentDate?: string | null;
  dueDate: string;
  assessorUserId?: string | null;
  overallScore?: number | null;
  areaScores?: AreaScore[] | null;
  categoryScores?: CategoryScore[] | null;
  responses?: MaturityResponse[] | null;
  status: MaturityAssessmentStatus;
  completionPercentage: number;
  approvedBy?: string | null;
  approvedAt?: string | null;
  /** True when created by the EventBridge recurring scheduler. */
  isScheduled?: boolean | null;
  /** True when Draft assessment has not been progressed for > overdueThresholdDays. */
  overdue?: boolean | null;
  ownerType: string;
  ownerId: string;
  _schemaVersion: string;
  _createdAt: string;
  _createdBy: string;
  _updatedAt: string;
  _updatedBy: string;
}

export interface MaturityFrameworkEdge {
  node: MaturityFramework;
  cursor: string;
}

export interface MaturityFrameworkConnection {
  edges: MaturityFrameworkEdge[];
  pageInfo: PageInfo;
  totalCount?: number | null;
}

export interface MaturityAssessmentEdge {
  node: MaturityAssessment;
  cursor: string;
}

export interface MaturityAssessmentConnection {
  edges: MaturityAssessmentEdge[];
  pageInfo: PageInfo;
  totalCount?: number | null;
}

export interface MaturityAssessmentFilter {
  subjectEntityType?: MaturitySubjectEntityType;
  subjectEntityId?: string;
  status?: MaturityAssessmentStatus;
  assessmentPeriod?: string;
  frameworkId?: string;
}

// ─── Maturity Trend / Comparison / Distribution ────────────────────────────
